import React, { useState, useRef, KeyboardEvent } from 'react';
import { Box, TextField, IconButton, CircularProgress } from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachIcon,
} from '@mui/icons-material';
import FilePreview from './FilePreview';

interface MessageInputProps {
  onSend: (content: string, files: File[]) => Promise<void>;
  onTyping?: () => void;
  disabled?: boolean;
  placeholder?: string;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

// Allowed file types
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'application/csv',
  'application/zip',
  'application/x-zip-compressed',
];

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

// Check if filename contains non-ASCII characters (Arabic, Chinese, etc.)
const hasNonAsciiChars = (str: string): boolean => {
  // eslint-disable-next-line no-control-regex
  return /[^\x00-\x7F]/.test(str);
};

// Sanitize filename by replacing non-ASCII names with a generated safe name
const sanitizeFilename = (filename: string): string => {
  const ext = filename.includes('.') ? filename.split('.').pop() || '' : '';
  const nameWithoutExt = ext ? filename.slice(0, -(ext.length + 1)) : filename;

  if (hasNonAsciiChars(nameWithoutExt)) {
    // Generate a safe filename: file_YYYYMMDD_HHMMSS_random
    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:T]/g, '').slice(0, 14);
    const random = Math.random().toString(36).substring(2, 8);
    return ext ? `file_${timestamp}_${random}.${ext}` : `file_${timestamp}_${random}`;
  }

  return filename;
};

// Create a new File object with sanitized filename
const createSanitizedFile = (file: File): File => {
  const sanitizedName = sanitizeFilename(file.name);
  if (sanitizedName !== file.name) {
    return new File([file], sanitizedName, { type: file.type });
  }
  return file;
};

const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  onTyping,
  disabled = false,
  placeholder = 'Type a message...',
}) => {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);

    // Emit typing indicator
    if (onTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      onTyping();
      typingTimeoutRef.current = setTimeout(() => {
        // Typing stopped
      }, 1000);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    const validFiles = selectedFiles.filter((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        console.warn(`File type not allowed: ${file.type}`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File too large: ${file.name}`);
        return false;
      }
      return true;
    });

    setFiles((prev) => [...prev, ...validFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if ((!message.trim() && files.length === 0) || sending || disabled) return;

    setSending(true);
    try {
      // Sanitize filenames before sending (replace Arabic/non-ASCII names)
      const sanitizedFiles = files.map(createSanitizedFile);
      await onSend(message.trim(), sanitizedFiles);
      setMessage('');
      setFiles([]);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = (message.trim() || files.length > 0) && !sending && !disabled;

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      {/* File previews */}
      {files.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {files.map((file, index) => (
            <FilePreview
              key={`${file.name}-${index}`}
              file={file}
              onRemove={() => handleRemoveFile(index)}
            />
          ))}
        </Box>
      )}

      {/* Input area */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1 }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* Attach button */}
        <IconButton
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || sending}
          sx={{ color: 'text.secondary' }}
        >
          <AttachIcon />
        </IconButton>

        {/* Text input */}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || sending}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: OUTLOOK_BLUE,
              },
            },
          }}
        />

        {/* Send button */}
        <IconButton
          onClick={handleSend}
          disabled={!canSend}
          sx={{
            backgroundColor: canSend ? OUTLOOK_BLUE : 'divider',
            color: 'white',
            '&:hover': {
              backgroundColor: canSend ? '#106ebe' : 'divider',
            },
            '&.Mui-disabled': {
              color: 'white',
            },
          }}
        >
          {sending ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default MessageInput;
